import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Text, TouchableOpacity, View } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
import FileIcon from '@/components/Files/FileIcon';
import EmptyState from '@/components/Files/EmptyState';
import { Query } from '@/utils/react-query/Queries';
import { formatSize } from '@/utils/FileManager';

/**
 * Reusable modal picker that traverses org file storage (Folders/Files).
 * onSelect receives a Files table record ({ id, key, filename, mimeType, size, ... }).
 * scope/ownerID select the storage scope — 'resources' + null ownerID is the org library.
 */
const FilePickerModal = ({ isOpen, onClose, onSelect, scope = 'resources', ownerID = null, title = 'Select a file' }) => {
    const { theme: { colors, fonts } } = useUnistyles();
    const [folderID, setFolderID] = useState('root');
    const [navStack, setNavStack] = useState([]);

    const { data: contents, isLoading } = Query.files.contents({
        scope,
        ownerID,
        folderID,
        options: { enabled: isOpen },
    });

    useEffect(() => {
        if (isOpen) {
            setFolderID('root');
            setNavStack([]);
        }
    }, [isOpen]);

    const items = useMemo(() => [
        ...(contents?.folders || []).map(folder => ({ ...folder, isFolder: true })),
        ...(contents?.files || []).map(file => ({ ...file, isFolder: false })),
    ], [contents]);

    const currentFolderName = navStack.length > 0 ? navStack[navStack.length - 1].name : null;

    const handleOpenFolder = (folder) => {
        setNavStack(prev => [...prev, { id: folder.id, name: folder.name }]);
        setFolderID(folder.id);
    };

    const handleBack = () => {
        const next = navStack.slice(0, -1);
        setNavStack(next);
        setFolderID(next.length > 0 ? next[next.length - 1].id : 'root');
    };

    const handleSelectFile = (file) => {
        onSelect?.(file);
        onClose?.();
    };

    const renderRow = ({ item }) => {
        if (item.isFolder) {
            return (
                <TouchableOpacity testID={`filepicker-folder-${item.id}`} style={styles.row} onPress={() => handleOpenFolder(item)}>
                    <Unicon name="folder" size={24} colorVariant="primary" library="material" />
                    <View style={styles.nameWrap}>
                        <Text style={[styles.name, { fontFamily: fonts.body, color: colors.text }]} numberOfLines={1}>{item.name}</Text>
                    </View>
                    <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
                </TouchableOpacity>
            );
        }
        return (
            <TouchableOpacity testID={`filepicker-file-${item.id}`} style={styles.row} onPress={() => handleSelectFile(item)}>
                <FileIcon filename={item.filename} mimeType={item.mimeType} size={24} color="textSecondary" />
                <View style={styles.nameWrap}>
                    <Text style={[styles.name, { fontFamily: fonts.body, color: colors.text }]} numberOfLines={1}>{item.filename}</Text>
                    <Text style={[styles.meta, { fontFamily: fonts.body, color: colors.textSecondary }]}>{formatSize(item.size || 0)}</Text>
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            cancelText="Cancel"
            scrollable={false}
        >
            <View style={styles.container}>
                {navStack.length > 0 && (
                    <TouchableOpacity testID="filepicker-back" style={styles.backRow} onPress={handleBack}>
                        <Unicon name="chevron-back" size={20} colorVariant="primary" />
                        <Text style={[styles.backText, { fontFamily: fonts.heading, color: colors.primary }]} numberOfLines={1}>
                            {currentFolderName}
                        </Text>
                    </TouchableOpacity>
                )}

                {isLoading ? (
                    <View style={styles.loadingWrap}>
                        <ActivityIndicator size="small" color={colors.primary} />
                    </View>
                ) : (
                    <FlatList
                        data={items}
                        keyExtractor={(item) => `${item.isFolder ? 'folder' : 'file'}-${item.id}`}
                        renderItem={renderRow}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={false}
                        ListEmptyComponent={<EmptyState message="No files or folders here" />}
                    />
                )}
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    container: {
        flex: 1,
    },
    backRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 8,
        paddingHorizontal: 12,
        marginBottom: 4,
        backgroundColor: colors.surface,
        borderRadius: 8,
        alignSelf: 'flex-start',
    },
    backText: {
        fontSize: 14,
        maxWidth: 300,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    nameWrap: {
        flex: 1,
    },
    name: {
        fontSize: 14,
    },
    meta: {
        fontSize: 10,
    },
    loadingWrap: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 40,
    },
}));

export default FilePickerModal;
